import { Helmet } from "react-helmet-async";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Building2, ExternalLink } from "lucide-react";

import { LandingHeader, LandingFooter } from "@/components/landing/shared";
import PublicOrgHeader from "@/components/org-story/PublicOrgHeader";
import PublicOrgFooter from "@/components/org-story/PublicOrgFooter";
import { PublicSignInProvider } from "@/components/org-story/publicSignIn";
import PublicLoadError from "@/components/org-story/PublicLoadError";
import { StandalonePage, StandalonePanel } from "@/components/marketing/StandalonePage";
import CanonicalTags from "@/components/seo/CanonicalTags";
import RichTextRenderer from "@/components/ui/rich-text-renderer";
import { Skeleton } from "@/components/ui/skeleton";
import { usePrerenderData } from "@/lib/prerenderData";
import { fetchPublicOrganizationArticle } from "@/lib/publicOrganizationRpc";
import { FEATURE_FLAGS } from "@/config/featureFlags";
import {
  articleDate,
  articleDescription,
  articleJsonLd,
  articleReadTime,
  articleTitle,
  contentTypeLabel,
  formatLongDate,
  httpUrl,
  organizationArticlePath,
  organizationPath,
  serializeJsonLd,
} from "@/lib/publicOrganizationProfile";
import { useRetiredSlugRedirect } from "@/hooks/useRetiredSlugRedirect";

/**
 * The PUBLIC newsroom article — /organization/:slug/news/:contentId for an
 * anonymous visitor or a crawler. Same contract as PublicOrganizationProfile:
 * renders only what get_public_organization_article returns, no gated
 * providers, prerenderable. The signed-in OrganizationNewsArticle keeps the
 * URL for users with a session (src/App.tsx).
 *
 * When the item has a `link`, it covers something published elsewhere: the
 * body here is the organization's own summary and the page links out to the
 * original, which JSON-LD names as `isBasedOn`.
 */

const PageShell = ({ children }: { children: React.ReactNode }) => (
  <div className="min-h-screen bg-background">
    <LandingHeader variant="main" />
    {children}
    <LandingFooter />
  </div>
);

const NotFound = ({ slug }: { slug: string }) => (
  <StandalonePage>
    <Helmet>
      <title>Article not found | Compaire</title>
      <meta name="robots" content="noindex" />
    </Helmet>
    <StandalonePanel
      tone="notice"
      title="Article not found"
      actions={
        slug && (
          <Link to={organizationPath(slug)} className="mk-btn mk-btn--outline">
            Back to the organization
          </Link>
        )
      }
    >
      <p className="mk-body">This article doesn't exist or hasn't been published yet.</p>
    </StandalonePanel>
  </StandalonePage>
);

const PublicOrganizationNewsArticle = () => {
  const { slug = "", contentId = "" } = useParams();
  const prerender = usePrerenderData();

  const initialData =
    prerender.article &&
    prerender.article.organization.slug === slug &&
    prerender.article.article.id === contentId
      ? prerender.article
      : undefined;

  const { data, isLoading, isError, isFetching, refetch } = useQuery({
    queryKey: ["public-organization-article", slug, contentId],
    queryFn: () => fetchPublicOrganizationArticle(slug, contentId),
    enabled: !!slug && !!contentId,
    initialData,
    staleTime: 5 * 60 * 1000,
  });

  const resolvingRedirect = useRetiredSlugRedirect(slug, !isLoading && !isError && data === null, `/news/${encodeURIComponent(contentId)}`);

  if (isLoading || resolvingRedirect) {
    const skeletons = (
      <>
        <Skeleton className="h-8 w-40" />
        <Skeleton className="aspect-[16/9] w-full rounded-2xl" />
        <Skeleton className="h-10 w-3/4" />
        <Skeleton className="h-40 w-full" />
      </>
    );

    /**
     * When `FEATURE_FLAGS.AGENT_UI_V2` is on, mirror the same bright
     * flex-column shell the flag-on found-article render below uses (see
     * `Loading` in PublicOrganizationProfile.tsx for the same pattern). When
     * the flag is off, the output stays byte-for-byte the pre-existing
     * skeleton.
     */
    if (FEATURE_FLAGS.AGENT_UI_V2) {
      return (
        <PublicSignInProvider>
          <div className="mk-app flex min-h-screen flex-col">
            <PublicOrgHeader />
            <main className="flex-1">
              <div className="mx-auto max-w-[760px] space-y-6 px-4 py-10 sm:px-[30px]">{skeletons}</div>
            </main>
            <PublicOrgFooter />
          </div>
        </PublicSignInProvider>
      );
    }

    return (
      <PageShell>
        <main className="mx-auto max-w-[760px] space-y-6 px-4 py-10 sm:px-[30px]">{skeletons}</main>
      </PageShell>
    );
  }
  // A failed lookup is not "not found" (issue #1063): retry, and no noindex.
  if (!data && isError) {
    return <PublicLoadError title="Article" onRetry={() => void refetch()} retrying={isFetching} />;
  }
  if (!data) return <NotFound slug={slug} />;

  const { organization: org, article } = data;
  const path = organizationArticlePath(org.slug, article.id);
  const date = articleDate(article);
  const readTime = articleReadTime(article);
  const sourceUrl = httpUrl(article.link);

  const head = (
    <>
      <CanonicalTags path={path} title={articleTitle(data)} description={articleDescription(data)} />
      <Helmet>
        <meta property="og:type" content="article" />
        {article.image_url && <meta property="og:image" content={article.image_url} />}
        {article.image_url && <meta name="twitter:image" content={article.image_url} />}
        {date && <meta property="article:published_time" content={date} />}
        <script type="application/ld+json">{serializeJsonLd(articleJsonLd(data))}</script>
      </Helmet>
    </>
  );

  /**
   * The 760 px article column, unchanged in either path. Extracted so it can
   * be mounted inside a semantic `<main>` in the flag-off shell, or inside a
   * plain `<div>` nested inside the flag-on `<main className="theme-bright
   * flex-1">` — never both, since a page must carry exactly one `<main>`
   * landmark.
   */
  const articleBody = (
    <>
      <nav aria-label="Breadcrumb" className="mb-6 text-[13px] text-ink-500">
        <Link to="/teams-brokerages" className="hover:text-ink-900">
          Teams &amp; Brokerages
        </Link>
        <span className="mx-2">/</span>
        <Link to={organizationPath(org.slug)} className="hover:text-ink-900">
          {org.name}
        </Link>
        <span className="mx-2">/</span>
        <span className="text-ink-700">{contentTypeLabel(article.type)}</span>
      </nav>

      <article>
        {article.image_url && (
          <img src={article.image_url} alt="" className="mb-8 aspect-[16/9] w-full rounded-2xl object-cover" />
        )}

        <div className="eyebrow mb-3 text-primary">{contentTypeLabel(article.type)}</div>
        <h1 className="m-0 font-display text-[30px] font-semibold leading-[1.12] tracking-[-0.02em] text-ink-900 sm:text-[38px]">
          {article.title}
        </h1>

        <div className="mt-6 flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-border pb-6 text-[13.5px] text-ink-500">
          {org.logo_url && (
            <img src={org.logo_url} alt="" className="h-8 w-8 rounded-md border border-border bg-card object-contain" />
          )}
          <Link to={organizationPath(org.slug)} className="font-medium text-ink-900 hover:underline">
            {org.name}
          </Link>
          {date && (
            <>
              <span aria-hidden="true">·</span>
              <time dateTime={date}>{formatLongDate(date)}</time>
            </>
          )}
          {readTime && (
            <>
              <span aria-hidden="true">·</span>
              <span>{readTime}</span>
            </>
          )}
        </div>

        <div className="mt-8">
          <RichTextRenderer content={article.body} className="text-[16px] leading-[1.7] text-ink-700" />
        </div>

        {sourceUrl && (
          <a
            href={sourceUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-8 inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
          >
            <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" /> Read the original source
          </a>
        )}
      </article>

      <div className="mt-12 border-t border-border pt-6">
        <Link
          to={organizationPath(org.slug)}
          className="inline-flex items-center gap-2 text-sm font-medium text-ink-700 hover:text-ink-900"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden="true" /> More from {org.name}
        </Link>
      </div>
    </>
  );

  /**
   * Same lesson as PublicOrganizationProfile: NOT `PageShell`, which has no
   * `flex-1` on its body and would leave a cream band below a short article.
   * This shell instead fills `theme-bright` to the footer. The former
   * `<main>` becomes a plain `<div>` here so the page still carries exactly
   * ONE `<main>` element — the flex-1 wrapper itself.
   */
  if (FEATURE_FLAGS.AGENT_UI_V2) {
    return (
      <PublicSignInProvider>
        <div className="mk-app flex min-h-screen flex-col">
          {/* The same logo bar as the organization's page this article belongs to. */}
          <PublicOrgHeader />
          {head}
          <main className="flex-1">
            <div className="mx-auto max-w-[760px] px-4 pb-20 pt-6 sm:px-[30px]">{articleBody}</div>
          </main>
          <PublicOrgFooter />
        </div>
      </PublicSignInProvider>
    );
  }

  return (
    <PageShell>
      {head}
      <main className="mx-auto max-w-[760px] px-4 pb-20 pt-6 sm:px-[30px]">{articleBody}</main>
    </PageShell>
  );
};

export default PublicOrganizationNewsArticle;
